/**
 * Log levels aligned with both Pino and .NET ILogger conventions.
 * Maps to backend: Trace=10, Debug=20, Information=30, Warning=40, Error=50, Critical=60
 */
export type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal'

/**
 * Numeric log level values matching Pino's internal representation.
 */
export const LogLevelValues: Record<LogLevel, number> = {
  trace: 10,
  debug: 20,
  info: 30,
  warn: 40,
  error: 50,
  fatal: 60,
}

/**
 * Configuration options for the logger.
 */
export interface LoggerOptions {
  // Minimum log level to output. Logs below this level are suppressed.
  level?: LogLevel
  name?: string
  // Enable structured JSON output (true for production, false for dev pretty-print)
  jsonOutput?: boolean
  defaultContext?: Record<string, unknown>
}

/**
 * Structured log entry format matching backend .NET ILogger JSON output.
 * This format enables unified log aggregation across frontend and backend.
 * TODO! When the tracing support is updated for OpenTelemetry and AWS X-Ray
 * we should sync this structure with the backend.
 */
export interface LogEntry {
  level: LogLevel
  time: number
  msg: string
  context?: string
  // Distributed trace ID for request correlation
  traceId?: string
  // Additional structured data
  [key: string]: unknown
}

/**
 * Logger interface providing structured logging methods.
 * Mirrors console API but with structured output and log levels.
 */
export interface Logger {
  // (most verbose)
  trace(msg: string, data?: Record<string, unknown>): void
  debug(msg: string, data?: Record<string, unknown>): void
  info(msg: string, data?: Record<string, unknown>): void
  warn(msg: string, data?: Record<string, unknown>): void
  error(msg: string, data?: Record<string, unknown>): void
  // (most severe)
  fatal(msg: string, data?: Record<string, unknown>): void
  // Create a child logger with additional bound context
  child(bindings: Record<string, unknown>): Logger
  level: LogLevel
  setLevel(level: LogLevel): void
}

export type Environment = 'dev' | 'development' | 'staging' | 'stage' | 'prod' | 'production'

/**
 * Default log levels per environment.
 */
export const EnvironmentLogLevels: Record<Environment, LogLevel> = {
  dev: 'debug',
  development: 'debug',
  staging: 'info',
  stage: 'info',
  prod: 'error',
  production: 'error',
}
